import axios from 'axios';
import { GroupCacheService } from './groupCache';

// This will be set by the ConfigProvider after loading runtime config
let API_BASE_URL = process.env.REACT_APP_FE_API_URL || 'http://localhost:3011';

// Utility function to extract group slug from current URL path
export const extractGroupSlugFromUrl = (): string | null => {
  try {
    const pathname = globalThis.location.pathname;
    
    // Handle various URL patterns:
    // /{group-slug}/dashboard
    // /{group-slug}/admin
    // /{group-slug}/my-lists
    // /{group-slug}/browse-lists
    // /{group-slug}/my-reservations
    
    // Remove leading slash and split by slash
    const pathSegments = pathname.replace(/^\/+/, '').split('/');
    
    // If we have at least one segment and it's not a known public route
    if (pathSegments.length >= 1 && pathSegments[0]) {
      const firstSegment = pathSegments[0];
      
      // Use centralized public route check
      const { isPublicRouteSegment } = require('../constants/routes');
      if (isPublicRouteSegment(firstSegment)) {
        return null;
      }
      
      // The first segment should be the group slug
      return firstSegment;
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to extract group slug from URL:', error);
    return null;
  }
};

// Utility function to validate group slug format
export const isValidGroupSlugFormat = (slug: string): boolean => {
  if (!slug || typeof slug !== 'string') {
    return false;
  }
  
  // Check if slug is reserved
  const { isReservedSlug } = require('../constants/routes');
  if (isReservedSlug(slug)) {
    return false;
  }
  
  // Group slugs should be alphanumeric with hyphens, 3-100 characters
  const slugPattern = /^[a-z0-9]+(-[a-z0-9]+)*$/i;
  return slugPattern.test(slug) && slug.length >= 3 && slug.length <= 100;
};

// Utility functions for error message handling
export const getGroupSelectionError = (): string | null => {
  try {
    const error = sessionStorage.getItem('groupSelectionError');
    if (error) {
      sessionStorage.removeItem('groupSelectionError');
      return error;
    }
    return null;
  } catch (error) {
    console.warn('Failed to get group selection error:', error);
    return null;
  }
};

export const clearGroupSelectionError = (): void => {
  try {
    sessionStorage.removeItem('groupSelectionError');
  } catch (error) {
    console.warn('Failed to clear group selection error:', error);
  }
};

// Function to update the API base URL (called by ConfigProvider)
export const setApiBaseUrl = (url: string) => {
  if (API_BASE_URL !== url) {
    API_BASE_URL = url;
    // Update axios default baseURL
    api.defaults.baseURL = url;
  }
};

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to ensure we always use the latest API_BASE_URL and add group context
api.interceptors.request.use(
  (config) => {
    // Ensure we're using the current API_BASE_URL
    if (config.baseURL !== API_BASE_URL) {
      config.baseURL = API_BASE_URL;
    }
    
    // Add group slug header for authenticated requests
    const groupSlug = extractGroupSlugFromUrl();
    if (groupSlug && isValidGroupSlugFormat(groupSlug)) {
      // Only add the header for protected endpoints (not auth endpoints)
      const isAuthEndpoint = config.url?.includes('/auth/') || false;
      const isPublicEndpoint = config.url?.includes('/groups') && config.method?.toLowerCase() === 'post'; // Group creation
      
      if (!isAuthEndpoint && !isPublicEndpoint) {
        config.headers = config.headers || {};
        config.headers['X-Group-Slug'] = groupSlug;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with comprehensive error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Import error handler dynamically to avoid circular dependencies
    let errorHandler: any;
    try {
      const { errorHandler: eh } = await import('../utils/errorHandler');
      errorHandler = eh;
    } catch (importError) {
      console.warn('Failed to import errorHandler:', importError);
    }
    
    // Handle group context related errors
    if (error.response?.status === 400 && error.response?.data?.message?.includes('Group context required')) {
      // 400 Bad Request: Missing group context - retry with group slug from URL
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        
        const groupSlug = extractGroupSlugFromUrl();
        if (groupSlug && isValidGroupSlugFormat(groupSlug)) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['X-Group-Slug'] = groupSlug;
          
          try {
            return await api.request(originalRequest);
          } catch (retryError) {
            // If retry fails, use error handler if available
            if (errorHandler) {
              errorHandler.handleGroupContextError(retryError, {
                component: 'APIService',
                action: 'retry_with_group_context',
                groupSlug,
              });
            } else {
              redirectToGroupSelection('Errore nel contesto del gruppo');
            }
            return Promise.reject(retryError);
          }
        } else {
          // No valid group slug in URL, use error handler if available
          const contextError = new Error('Missing group context in URL');
          if (errorHandler) {
            errorHandler.handleGroupContextError(contextError, {
              component: 'APIService',
              action: 'missing_group_context',
            });
          } else {
            redirectToGroupSelection('Contesto del gruppo mancante');
          }
        }
      }
    }
        
    if (error.response?.status === 404 && error.response?.data?.message?.includes('Gruppo non trovato')) {
      // 404 Not Found: Group doesn't exist
      const groupSlug = extractGroupSlugFromUrl();
      if (groupSlug) {
        // Clear invalid group from localStorage
        try {
          const { LocalStorageManager } = await import('./localStorage');
          LocalStorageManager.clearGroupPreference();
        } catch (importError) {
          console.warn('Failed to import LocalStorageManager:', importError);
        }
        
        if (errorHandler) {
          errorHandler.handleGroupContextError(error, {
            component: 'APIService',
            action: 'group_not_found',
            groupSlug,
          });
        } else {
          redirectToGroupSelection('Il gruppo richiesto non esiste');
        }
      }
    }
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      if (errorHandler) {
        errorHandler.handleAuthError(error, {
          component: 'APIService',
          action: 'unauthorized',
        });
      }
      // Don't automatically redirect on 401 - let error handler manage it
    }
    
    // For other errors, let them bubble up to be handled by error boundaries
    return Promise.reject(error);
  }
);

// Helper function to redirect to group selection with error message
const redirectToGroupSelection = (errorMessage: string) => {
  // Store error message in sessionStorage for display on group selection page
  try {
    sessionStorage.setItem('groupSelectionError', errorMessage);
  } catch (storageError) {
    console.warn('Failed to store error message:', storageError);
  }
  
  // Redirect to group selection page (but not from public pages)
  const currentPath = globalThis.location.pathname;
  const { isPublicRoute } = require('../constants/routes');
  
  if (!isPublicRoute(currentPath)) {
    globalThis.location.href = '/group-selection';
  }
};

// API Service interface
interface ApiService {
  api: typeof api;
  getCurrentSession(): Promise<any>;
  login(data: { email: string }): Promise<any>;
  autoLogin(data: { email: string; groupSlug: string }): Promise<any>;
  logout(): Promise<any>;
  verifyMagicLink(token: string): Promise<any>;
  createGroup(data: any): Promise<any>;
  getUserGroups(): Promise<any>;
  getGroupDetails(slug: string): Promise<any>;

  createListItem(personId: number, data: any): Promise<any>;
  updateListItem(itemId: number, data: any): Promise<any>;
  deleteListItem(itemId: number): Promise<any>;
  getMyReservations(personId: number): Promise<any>;
  getMyReservationsForEvent(personId: number, eventId: number): Promise<any>;
  reserveItem(itemId: number, personId: number): Promise<any>;
  cancelReservation(reservationId: number): Promise<any>;
  createEvent(data: any): Promise<any>;
  getGroupEvents(status?: string): Promise<any>;
  getEvent(eventId: number): Promise<any>;
  getEventLists(eventId: number): Promise<any>;
  getPersonListForEvent(eventId: number, personId: number): Promise<any>;
  createListItemForEventPerson(eventId: number, personId: number, data: any): Promise<any>;
  getEventListStats(eventId: number): Promise<any>;
  getPersonListStatsForEvent(eventId: number, personId: number): Promise<any>;
  updateEventStatus(eventId: number, data: any): Promise<any>;
  deleteEvent(eventId: number): Promise<any>;
  activatePersonForEvent(eventId: number, personId: number, data: any): Promise<any>;
  deactivatePersonForEvent(eventId: number, personId: number, data: any): Promise<any>;
  lockList(listId: number): Promise<any>;
  checkPastUnreservedGifts(eventId: number, personId: number): Promise<any>;
  copyPastUnreservedGifts(eventId: number, personId: number): Promise<any>;
  declinePastGiftsCopy(eventId: number, personId: number): Promise<any>;
}

// API Service with all the methods expected by the frontend
export const apiService: ApiService = {
  // Expose the axios instance for direct use
  api,

  // Auth methods
  async getCurrentSession() {
    const response = await api.get('/auth/session');
    return response.data;
  },

  async login(data: { email: string }) {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  async autoLogin(data: { email: string; groupSlug: string }) {
    const response = await api.post('/auth/auto-login', data);
    return response.data;
  },

  async logout() {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  async verifyMagicLink(token: string) {
    const response = await api.get(`/auth/verify/${token}`);
    return response.data;
  },

  // Group methods
  async createGroup(data: any) {
    const response = await api.post('/groups', data);
    
    // Clear group list cache since a new group was created
    GroupCacheService.invalidateAllGroupCache();
    
    return response.data;
  },

  async getUserGroups() {
    const response = await api.get('/groups');
    // Transform the response to match the expected format
    const groups = response.data.map((item: any) => ({
      id: item.group.id,
      slug: item.group.slug,
      name: item.group.name,
      isAdmin: item.isAdmin,
      memberCount: item.memberCount,
      createdAt: item.group.createdAt,
    }));
    return { groups };
  },

  async getGroupDetails(slug: string) {
    // Check cache first
    const cachedData = GroupCacheService.getCachedGroupDetails(slug);
    if (cachedData) {
      return cachedData;
    }

    // Fetch from API if not cached
    const response = await api.get(`/groups/${slug}`);
    
    // Cache the response
    GroupCacheService.cacheGroupDetails(slug, response.data);
    
    return response.data;
  },

  // Event-based list methods
  async getEventLists(eventId: number) {
    const response = await api.get(`/lists/event/${eventId}`);
    return response.data;
  },

  async getPersonListForEvent(eventId: number, personId: number) {
    const response = await api.get(`/lists/event/${eventId}/person/${personId}`);
    return response.data;
  },

  async createListItemForEventPerson(eventId: number, personId: number, data: any) {
    const response = await api.post(`/lists/event/${eventId}/person/${personId}/items`, data);
    return response.data;
  },

  async getEventListStats(eventId: number) {
    const response = await api.get(`/lists/event/${eventId}/stats`);
    return response.data;
  },

  async getPersonListStatsForEvent(eventId: number, personId: number) {
    const response = await api.get(`/lists/event/${eventId}/person/${personId}/stats`);
    return response.data;
  },



  async createListItem(personId: number, data: any) {
    const response = await api.post(`/lists/person/${personId}/items`, data);
    return response.data;
  },

  async updateListItem(itemId: number, data: any) {
    const response = await api.put(`/lists/items/${itemId}`, data);
    return response.data;
  },

  async deleteListItem(itemId: number) {
    const response = await api.delete(`/lists/items/${itemId}`);
    return response.data;
  },

  // Reservation methods
  async getMyReservations(personId: number) {
    const response = await api.get(`/reservations/person/${personId}`);
    return response.data;
  },

  async getMyReservationsForEvent(personId: number, eventId: number) {
    const response = await api.get(`/reservations/person/${personId}/event/${eventId}`);
    return response.data;
  },

  async reserveItem(itemId: number, personId: number) {
    const response = await api.post('/reservations', { 
      listItemId: itemId, 
      reservedByPersonId: personId 
    });
    
    return response.data;
  },

  async cancelReservation(reservationId: number) {
    const response = await api.delete(`/reservations/${reservationId}`);
    
    return response.data;
  },

  // Event methods
  async createEvent(data: any) {
    const response = await api.post('/events', data);
    
    // Invalidate group details cache since event data changed
    const groupSlug = extractGroupSlugFromUrl();
    if (groupSlug) {
      GroupCacheService.invalidateGroupCache(groupSlug);
    }
    
    return response.data;
  },

  async getGroupEvents(status?: string) {
    const params = status ? { status } : {};
    const response = await api.get('/events', { params });
    return response.data;
  },

  async getEvent(eventId: number) {
    const response = await api.get(`/events/${eventId}`);
    return response.data;
  },



  async updateEventStatus(eventId: number, data: any) {
    const response = await api.patch(`/events/${eventId}/status`, data);
    return response.data;
  },

  async deleteEvent(eventId: number) {
    const response = await api.delete(`/events/${eventId}`);
    return response.data;
  },

  async activatePersonForEvent(eventId: number, personId: number, data: any) {
    const response = await api.post(`/events/${eventId}/lists/${personId}`, data);
    return response.data;
  },

  async deactivatePersonForEvent(eventId: number, personId: number, data: any) {
    const response = await api.delete(`/events/${eventId}/lists/${personId}`, { data });
    return response.data;
  },

  // List locking methods
  async lockList(listId: number) {
    const response = await api.post(`/lists/${listId}/lock`);
    
    return response.data;
  },

  async checkPastUnreservedGifts(eventId: number, personId: number) {
    const response = await api.get(`/lists/event/${eventId}/person/${personId}/past-gifts/check`);
    return response.data;
  },

  async copyPastUnreservedGifts(eventId: number, personId: number) {
    const response = await api.post(`/lists/event/${eventId}/person/${personId}/past-gifts/copy`);
    return response.data;
  },

  async declinePastGiftsCopy(eventId: number, personId: number) {
    const response = await api.post(`/lists/event/${eventId}/person/${personId}/past-gifts/decline`);
    return response.data;
  },
};

export default api;